import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormDetailResponse, FormFieldDefinition } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { formsApi } from '@/services/forms.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { FieldEditor } from './field-editor';

/**
 * One form: its fields, its lifecycle, and the submissions it has collected.
 *
 * The field editor only appears while the form is a draft — the same rule
 * the API enforces, made visible rather than discovered through a rejected
 * request.
 */
export function FormDetailPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['forms', id],
    queryFn: ({ signal }) => formsApi.get(id, signal),
  });

  const submissionsQuery = useQuery({
    queryKey: ['forms', id, 'submissions'],
    queryFn: ({ signal }) => formsApi.listSubmissions(id, signal),
    enabled: query.isSuccess,
  });

  const onChanged = async (form: FormDetailResponse, message: string | null) => {
    queryClient.setQueryData(['forms', id], form);
    await queryClient.invalidateQueries({ queryKey: ['forms'], exact: false });
    setNotice(message);
  };

  const saveFields = useMutation({
    mutationFn: (fields: readonly FormFieldDefinition[]) => formsApi.updateFields(id, fields),
    onSuccess: (form) => onChanged(form, t('forms.form.fieldsSaved')),
  });

  const publish = useMutation({
    mutationFn: () => formsApi.publish(id),
    onSuccess: (form) =>
      onChanged(form, t('forms.form.published', { values: { title: form.title } })),
  });

  const archive = useMutation({
    mutationFn: (reason: string) => formsApi.archive(id, reason),
    onSuccess: (form) =>
      onChanged(form, t('forms.form.archived', { values: { title: form.title } })),
  });

  const restore = useMutation({
    mutationFn: () => formsApi.restore(id),
    onSuccess: (form) =>
      onChanged(form, t('forms.form.restored', { values: { title: form.title } })),
  });

  if (query.isPending) return <Spinner label={t('common.states.loading')} />;

  if (query.isError) {
    return (
      <div className="page">
        <Link to="/forms" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Alert tone="danger">{describeError(query.error, t)}</Alert>
      </div>
    );
  }

  const form = query.data;
  const isArchived = form.archivedAt !== null;
  const isDraft = form.status === 'DRAFT';
  const submissions = submissionsQuery.data?.data ?? [];

  const mutationError = [saveFields, publish, archive, restore].find((m) => m.isError);

  return (
    <div className="page">
      <Link to="/forms" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{form.title}</h1>
          <StatusBadge status={form.status} label={t(`forms.status.${form.status}`)} />
        </div>

        <div className="page-actions">
          {can('forms.form.publish') && isDraft && !isArchived && (
            <Button
              variant="secondary"
              loading={publish.isPending}
              onClick={() => publish.mutate()}
            >
              {t('forms.form.publish')}
            </Button>
          )}
          {can('forms.form.archive') &&
            (isArchived ? (
              <Button
                variant="secondary"
                loading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                {t('common.actions.restore')}
              </Button>
            ) : (
              <ArchiveButton form={form} onConfirm={(reason) => archive.mutate(reason)} />
            ))}
        </div>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}
      {mutationError && <Alert tone="danger">{describeError(mutationError.error, t)}</Alert>}
      {isArchived && <Alert tone="warning">{t('forms.form.archivedHint')}</Alert>}
      {!isArchived && isDraft && form.fields.length === 0 && (
        <Alert tone="warning">{t('forms.form.needsFieldsHint')}</Alert>
      )}

      <dl className="detail-grid">
        <Detail label={t('forms.form.description')} value={form.description} />
        <Detail
          label={t('common.labels.createdAt')}
          value={formatDateTime(form.createdAt, locale)}
          dir="ltr"
        />
      </dl>

      <section>
        <header className="page-header">
          <h2 className="page-title">{t('forms.form.fields')}</h2>
        </header>

        {isDraft && !isArchived && can('forms.form.update') ? (
          <FieldEditor
            fields={form.fields}
            saving={saveFields.isPending}
            onSave={(fields) => saveFields.mutate(fields)}
          />
        ) : form.fields.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <ul className="field-list">
            {form.fields.map((field) => (
              <li key={field.id}>
                <strong>{field.label}</strong> — {t(`forms.fieldType.${field.type}`)}
                {field.required && ` · ${t('forms.form.required')}`}
                {field.options && field.options.length > 0 && ` (${field.options.join(', ')})`}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="roster">
        <header className="page-header">
          <h2 className="page-title">
            {t('forms.submission.title')} ·{' '}
            {t('forms.form.submissionCount', { count: form.submissionCount })}
          </h2>
        </header>

        {submissions.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('people.person.singular')}</th>
                  <th scope="col">{t('forms.submission.answers')}</th>
                  <th scope="col">{t('forms.submission.submittedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr key={submission.id}>
                    <td>
                      {submission.personId ? (
                        <Link to={`/people/${submission.personId}`} className="person-name">
                          {submission.displayName}
                        </Link>
                      ) : (
                        <span className="cell-empty">{t('forms.submission.anonymous')}</span>
                      )}
                    </td>
                    <td>
                      <AnswerSummary fields={form.fields} answers={submission.answers} />
                    </td>
                    <td dir="ltr">{formatDateTime(submission.submittedAt, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// --- Pieces ------------------------------------------------------------------

function Detail({ label, value, dir }: { label: string; value: string | null; dir?: 'ltr' }) {
  return (
    <div className="detail-row">
      <dt className="detail-label">{label}</dt>
      <dd className="detail-value" dir={dir}>
        {value ?? <span className="cell-empty">—</span>}
      </dd>
    </div>
  );
}

function AnswerSummary({
  fields,
  answers,
}: {
  fields: readonly FormFieldDefinition[];
  answers: Readonly<Record<string, unknown>>;
}) {
  return (
    <span>
      {fields
        .filter((field) => answers[field.id] !== undefined && answers[field.id] !== null)
        .map((field) => `${field.label}: ${String(answers[field.id])}`)
        .join(' · ')}
    </span>
  );
}

function ArchiveButton({
  form,
  onConfirm,
}: {
  form: FormDetailResponse;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <Button variant="danger" onClick={() => setIsOpen(true)}>
        {t('common.actions.archive')}
      </Button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('forms.form.archiveConfirm', { values: { title: form.title } })}</p>
      <Field
        label={t('forms.form.archiveReason')}
        name="reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="danger" disabled={reason.trim().length === 0}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
